/**
 * JobQueue — flat, continuous, sortable job table for the Operations command center.
 *
 * Quick 260721 — SUPERSEDES the collapsible status-grouped layout. There are no
 * expand/collapse group headers anymore; every job is a row in one flat list.
 * Column order mirrors the legacy system:
 *   checkbox · Customer · Site · Driver · Ticket ID · Created by · Requested ·
 *   Due date · Type · Status · Actions
 * Status is a real, sortable column (colored badge). The status filter defaults to
 * an "Active" preset (excludes closed/billing/review states); "All statuses" and
 * each individual status are still selectable. Each row's dispatcher notes are
 * editable inline via a click-to-expand cell (no per-row save button) backed by the
 * same PATCH /v1/jobs/:id the detail modal uses (useUpdateJob).
 *
 * Filter state in URL search params (deep-linkable per D-07). Bulk-assign via
 * checkbox selection → BulkAssignBar. Rejected rows: danger left-border + overflow
 * menu (DISPATCH_MANAGER tier) → ForceAssignModal.
 *
 * Spec: UI-SPEC §3 (Job Queue); §7 (rejection + force-assign); DISPATCH-01/05.
 */

import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { MoreHorizontal, StickyNote } from 'lucide-react';
import { useApiOpts } from '@lodex-pro/web-shared';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@lodex-pro/web-shared/src/components/ui/dropdown-menu';
import { TooltipProvider } from '@lodex-pro/web-shared/src/components/ui/tooltip';
import { Skeleton } from '@lodex-pro/web-shared/src/components/ui/skeleton';
import { Checkbox } from '@lodex-pro/web-shared/src/components/ui/checkbox';
import { useJobs, type DispatchJob, type JobFilters } from './use-jobs';
import { useUpdateJob } from './use-update-job';
import {
  STATUS_ORDER,
  DISPATCHER_FILTER_STATUSES,
  statusFullLabel,
  statusColor,
} from './job-status';
import { JobDetailModal } from './JobDetailModal';
import { SiteDetailModal, type SiteRef } from './SiteDetailModal';
import { RequestorDetailModal } from '../requestors/RequestorDetailModal';
import { OperatorRequestNote, hasOperatorRequestNote } from './OperatorRequestNote';
import { BulkAssignBar } from './BulkAssignBar';
import { ForceAssignModal } from './ForceAssignModal';
import { useRoleCode } from '../../lib/use-role-code';
import { formatTimestamp } from '../../lib/timestamp';
import { useUserTimezone } from '../../lib/use-user-timezone';
import { FadeScroll } from '../../components/FadeScroll';
// Elevated roles that can see the "Force Assign Back" menu item (DEC-313 union applied).
import { FORCE_ASSIGN_ROLES } from '../../lib/role-gates';

// Quick 260721 (#7) — the "Active" preset: every status EXCEPT the closed /
// billing / review states. Mirrors the exclusion the dispatcher works by; the
// dropdown defaults to this, with "All statuses" + each individual status still
// selectable.
const STATUS_EXCLUDED_FROM_ACTIVE: ReadonlySet<string> = new Set([
  'COMPLETED',
  'INVOICED',
  'CANCELLED',
  'TO_BE_INVOICED',
  'IN_REVIEW',
]);
export const ACTIVE_STATUS_CODES: readonly string[] = STATUS_ORDER.filter(
  (s) => !STATUS_EXCLUDED_FROM_ACTIVE.has(s),
);
const STATUS_ORDER_INDEX: ReadonlyMap<string, number> = new Map(STATUS_ORDER.map((s, i) => [s, i]));

/**
 * The `status` CSV the board sends for a given `?status=` preset (86bazkpgy follow-up).
 *
 * "All" sends every status EXPLICITLY, including the two terminal ones. It used to send
 * nothing at all, which is not the same thing: `GET /v1/jobs` with no status filter falls
 * back to `NOT IN ('COMPLETED','CANCELLED')`, so "All statuses" quietly excluded exactly the
 * two a dispatcher picks "All" to see — and with search now reaching the server, looking for
 * a finished ticket returned nothing while looking like "no matches".
 *
 * Fixed HERE rather than by changing that server default on purpose: `/v1/jobs` is shared
 * with mobile, whose lists rely on the default meaning active-only. Changing a shared default
 * to fix one caller is the OPEN-055 / OPEN-056 shape of bug — this is a preset value, not
 * endpoint behaviour.
 *
 * Returns `undefined` only when there is genuinely no status constraint to send.
 */
export function statusCsvForPreset(statusParam: string | null): string | undefined {
  if (statusParam === null) return ACTIVE_STATUS_CODES.join(','); // default: Active preset
  if (statusParam === 'all') return STATUS_ORDER.join(',');
  return statusParam; // a single explicitly-chosen status code
}

// Column sort keys. Customer→operatorName, "Created by"→createdByName,
// Requested→createdAt, Due date→scheduledAt, Status→statusCode (lifecycle order).
type SortKey =
  | 'operatorName'
  | 'siteName'
  | 'driverName'
  | 'haulId'
  | 'createdByName'
  | 'requestorName'
  | 'createdAt'
  | 'scheduledAt'
  | 'jobTypeLabel'
  | 'statusCode';

const QUEUE_COLUMNS: ReadonlyArray<{ label: string; sortKey: SortKey | null }> = [
  { label: '', sortKey: null }, // checkbox
  { label: 'Customer', sortKey: 'operatorName' },
  { label: 'Site', sortKey: 'siteName' },
  { label: 'Driver', sortKey: 'driverName' },
  { label: 'Ticket ID', sortKey: 'haulId' },
  // 86bazkeyn — the customer contact who called the load in (jobs.requestor_id), clickable
  // through to their detail popup. Sits BEFORE "Created by" because who asked for the work is
  // the operationally interesting one; who keyed it in is provenance.
  { label: 'Requestor', sortKey: 'requestorName' },
  // OPEN-053 — relabelled from "Requestor". This column has always shown the DISPATCHER who
  // created the ticket (createdByName); "Requestor" now means the customer contact who called
  // the load in, which is a different person entirely.
  { label: 'Created by', sortKey: 'createdByName' },
  { label: 'Requested', sortKey: 'createdAt' },
  { label: 'Due date', sortKey: 'scheduledAt' },
  { label: 'Type', sortKey: 'jobTypeLabel' },
  { label: 'Status', sortKey: 'statusCode' },
  { label: 'Actions', sortKey: null },
];
const COLUMN_COUNT = QUEUE_COLUMNS.length;

/** Stable, null-last comparator over a DispatchJob field. Status sorts by
 *  lifecycle order (STATUS_ORDER), everything else by natural/numeric string order. */
function compareJobs(a: DispatchJob, b: DispatchJob, key: SortKey, dir: 'asc' | 'desc'): number {
  if (key === 'statusCode') {
    const ai = STATUS_ORDER_INDEX.get(a.statusCode) ?? STATUS_ORDER.length;
    const bi = STATUS_ORDER_INDEX.get(b.statusCode) ?? STATUS_ORDER.length;
    const r = ai - bi;
    return dir === 'asc' ? r : -r;
  }
  const av = a[key];
  const bv = b[key];
  if (av == null && bv == null) return 0;
  if (av == null) return 1; // nulls always last
  if (bv == null) return -1;
  const r = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
  return dir === 'asc' ? r : -r;
}

const cellStyle: React.CSSProperties = {
  padding: '8px 6px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: 'var(--color-text-primary)',
};

/**
 * Inline dispatcher-notes editor (Quick 260721 #8). Save-on-blur AND a 600ms
 * debounce while typing, via the shared useUpdateJob mutation (notes-only PATCH).
 * Local state is seeded from the server value ONCE — the 30s poll refetch never
 * clobbers an in-progress edit (the dispatcher's active edit wins).
 */
function JobNotesEditor({
  jobId,
  initialNotes,
}: {
  readonly jobId: string;
  readonly initialNotes: string;
}): React.JSX.Element {
  const [text, setText] = React.useState(initialNotes);
  const lastSaved = React.useRef(initialNotes);
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const mutation = useUpdateJob(jobId);

  const save = React.useCallback(
    (value: string): void => {
      const trimmed = value.trim();
      if (trimmed === lastSaved.current.trim()) return; // no-op if unchanged
      lastSaved.current = value;
      mutation.mutate({ dispatcherNotes: trimmed === '' ? null : value });
    },
    [mutation],
  );

  React.useEffect(
    () => (): void => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <textarea
        aria-label="Dispatcher notes"
        value={text}
        placeholder="Add dispatcher notes…"
        onChange={(e): void => {
          const v = e.target.value;
          setText(v);
          if (timer.current !== null) clearTimeout(timer.current);
          timer.current = setTimeout(() => save(v), 600);
        }}
        onBlur={(): void => {
          if (timer.current !== null) clearTimeout(timer.current);
          save(text);
        }}
        onClick={(e): void => e.stopPropagation()}
        rows={2}
        style={{
          width: '100%',
          resize: 'vertical',
          fontSize: '13px',
          fontFamily: 'inherit',
          lineHeight: 1.4,
          padding: '6px 8px',
          backgroundColor: 'var(--color-bg-tertiary)',
          border: '1px solid var(--color-border-secondary)',
          borderRadius: '10px',
          color: 'var(--color-text-primary)',
        }}
      />
      <span style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>
        {mutation.isPending ? 'Saving…' : 'Saved automatically'}
      </span>
    </div>
  );
}

export function JobQueue(): React.JSX.Element {
  const apiOpts = useApiOpts();
  const [searchParams, setSearchParams] = useSearchParams();
  const roleCode = useRoleCode();

  // ─── Filter state (URL search params — D-07 deep-linkable) ────────────────
  // status param: absent → "Active" preset (default); 'all' → no status filter;
  // a specific code → that status only.
  const statusParam = searchParams.get('status'); // null | 'all' | <code>
  // 86bazkpgy — was `?driver=`, which the server ignored entirely. Renamed to `?q=` with the
  // old name still read, so an existing bookmark keeps working; it simply starts working.
  const searchFilter = searchParams.get('q') ?? searchParams.get('driver') ?? '';
  const flagFilter = searchParams.get('flagged') === 'true';
  const rejectedFilter = searchParams.get('rejected') === 'true';
  const unassignedFilter = searchParams.get('unassigned') === 'true';

  const filters: JobFilters = React.useMemo((): JobFilters => {
    const statusCsv = statusCsvForPreset(statusParam);
    return {
      ...(statusCsv !== undefined && statusCsv.length > 0 ? { status: statusCsv } : {}),
      ...(searchFilter.length > 0 ? { q: searchFilter } : {}),
    };
  }, [statusParam, searchFilter]);

  const { data, isLoading, isFetching, isError, dataUpdatedAt } = useJobs(apiOpts, filters);
  const allJobs = React.useMemo<readonly DispatchJob[]>(() => data?.jobs ?? [], [data]);
  // DEC-066 — refresh "Updated" time in the dispatcher's local tz, 12-hour AM/PM.
  const tz = useUserTimezone();
  const updatedLabel = React.useMemo(
    () =>
      dataUpdatedAt
        ? new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
          }).format(new Date(dataUpdatedAt))
        : null,
    [dataUpdatedAt, tz],
  );

  // ─── Apply client-side filters ────────────────────────────────────────────
  const visibleJobs = React.useMemo(() => {
    let jobs = allJobs;
    if (rejectedFilter) {
      jobs = jobs.filter((j) => j.statusCode === 'REJECTED');
    }
    if (flagFilter) {
      jobs = jobs.filter((j) => j.statusCode === 'REJECTED' || j.statusCode === 'IN_REVIEW');
    }
    if (unassignedFilter) {
      jobs = jobs.filter((j) => !j.driverId);
    }
    return jobs;
  }, [allJobs, flagFilter, rejectedFilter, unassignedFilter]);

  // ─── Selection (multi-select) ─────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = React.useState<ReadonlySet<string>>(new Set());
  const [selectedJobId, setSelectedJobId] = React.useState<string | null>(null);
  // Quick 260722 — the Site detail popup target (null = closed).
  const [selectedSite, setSelectedSite] = React.useState<SiteRef | null>(null);
  // 86bazkeyn — the Requestor column's popup. Carries the name off the row as well as the id,
  // so the dialog has something to show while the catalog record loads (or if the requestor
  // has since been removed and only the job's snapshot survives).
  const [requestorPopup, setRequestorPopup] = React.useState<{
    id: string | null;
    name: string | null;
  } | null>(null);
  // Quick 260721 (#8) — which rows have their inline notes editor expanded.
  const [notesOpenIds, setNotesOpenIds] = React.useState<ReadonlySet<string>>(new Set());

  const toggleSelect = React.useCallback((id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = React.useCallback((): void => {
    setSelectedIds(new Set());
  }, []);

  const toggleNotes = React.useCallback((id: string): void => {
    setNotesOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // ─── Force-assign modal state ─────────────────────────────────────────────
  const [forceAssignJob, setForceAssignJob] = React.useState<{
    id: string;
    haulId: string;
    operatorRequestNotes: string | null;
  } | null>(null);

  // ─── Column-header sorting (flat list) ────────────────────────────────────
  const [sort, setSort] = React.useState<{ key: SortKey; dir: 'asc' | 'desc' } | null>(null);
  const toggleSort = React.useCallback((key: SortKey): void => {
    setSort((prev) =>
      prev && prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' },
    );
  }, []);
  const sortedJobs = React.useMemo(() => {
    if (!sort) return visibleJobs;
    return [...visibleJobs].sort((a, b) => compareJobs(a, b, sort.key, sort.dir));
  }, [visibleJobs, sort]);

  const isElevated = roleCode !== null && FORCE_ASSIGN_ROLES.has(roleCode);

  // Debounce the search box — only hits the URL after typing pauses.
  const [searchInput, setSearchInput] = React.useState(searchFilter);
  React.useEffect(() => {
    setSearchInput(searchFilter);
  }, [searchFilter]);
  React.useEffect(() => {
    if (searchInput === searchFilter) return;
    const t = setTimeout(() => {
      const p = new URLSearchParams(searchParams);
      if (searchInput.length > 0) p.set('q', searchInput);
      else p.delete('q');
      // Drop the superseded `driver` key so a migrated bookmark does not keep
      // re-seeding the old value on the next read.
      p.delete('driver');
      setSearchParams(p);
    }, 400);
    return () => {
      clearTimeout(t);
    };
  }, [searchInput, searchFilter, searchParams, setSearchParams]);

  // "Filtered" = narrowed beyond the default Active view (any explicit status
  // choice, a search term, or a flag/rejected/unassigned toggle).
  const hasFilters =
    statusParam !== null ||
    searchFilter.length > 0 ||
    flagFilter ||
    rejectedFilter ||
    unassignedFilter;
  const emptyMessage = hasFilters ? 'No jobs match your filters' : 'Queue is clear';

  const clearFilters = (): void => {
    setSearchInput('');
    setSearchParams(new URLSearchParams());
  };

  return (
    <TooltipProvider>
      <div
        style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}
      >
        {/* Auto-refresh indicator — persistent, bottom-right. */}
        <span
          aria-live="polite"
          style={{
            position: 'absolute',
            bottom: '8px',
            right: '8px',
            zIndex: 5,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '3px 10px',
            fontSize: '11px',
            color: 'var(--color-text-secondary)',
            backgroundColor: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border-primary)',
            borderRadius: 'var(--radius-pill)',
            pointerEvents: 'none',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              backgroundColor: isFetching ? 'var(--color-accent-blue)' : 'var(--color-success)',
              flexShrink: 0,
            }}
          />
          {isFetching
            ? 'Refreshing…'
            : updatedLabel !== null
              ? `Updated ${updatedLabel}`
              : 'Live · 30s'}
        </span>

        {/* Filters toolbar */}
        <div
          style={{
            display: 'flex',
            gap: '8px',
            padding: '8px 16px',
            borderBottom: '1px solid var(--color-border-primary)',
            backgroundColor: 'var(--color-bg-secondary)',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          {/* Status filter — defaults to the "Active" preset (#7). */}
          <select
            aria-label="Filter by status"
            value={statusParam ?? 'ACTIVE'}
            onChange={(e): void => {
              const v = e.target.value;
              const newParams = new URLSearchParams(searchParams);
              if (v === 'ACTIVE') {
                newParams.delete('status'); // default
              } else {
                newParams.set('status', v); // 'all' or a specific code
              }
              setSearchParams(newParams);
            }}
            style={{
              fontSize: '12px',
              padding: '4px 8px',
              backgroundColor: 'var(--color-bg-tertiary)',
              border: '1px solid var(--color-border-secondary)',
              borderRadius: '10px',
              color: 'var(--color-text-primary)',
              cursor: 'pointer',
            }}
          >
            <option value="ACTIVE">Active</option>
            <option value="all">All statuses</option>
            {DISPATCHER_FILTER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {statusFullLabel(s)}
              </option>
            ))}
          </select>

          {/* Unified search — debounced. Server-side across driver, site, requestor,
              customer and haul id (86bazkpgy). Was a "Driver…" box the server ignored. */}
          <input
            type="search"
            aria-label="Search driver, site, requestor, ticket or reference/PO number"
            placeholder="Search driver, site, requestor, PO…"
            value={searchInput}
            onChange={(e): void => {
              setSearchInput(e.target.value);
            }}
            style={{
              fontSize: '12px',
              padding: '4px 8px',
              backgroundColor: 'var(--color-bg-tertiary)',
              border: '1px solid var(--color-border-secondary)',
              borderRadius: '10px',
              color: 'var(--color-text-primary)',
              // Wider than the 120px the old "Driver…" box used: the placeholder now has to
              // say what the search covers, and a truncated hint is how a dispatcher keeps
              // believing it only searches drivers.
              width: '230px',
            }}
          />

          {/* Rejected toggle */}
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              color: 'var(--color-text-secondary)',
            }}
          >
            <input
              type="checkbox"
              checked={rejectedFilter}
              onChange={(e): void => {
                const newParams = new URLSearchParams(searchParams);
                if (e.target.checked) newParams.set('rejected', 'true');
                else newParams.delete('rejected');
                setSearchParams(newParams);
              }}
            />
            Rejected
          </label>

          {/* Unassigned toggle */}
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              color: 'var(--color-text-secondary)',
            }}
          >
            <input
              type="checkbox"
              checked={unassignedFilter}
              onChange={(e): void => {
                const newParams = new URLSearchParams(searchParams);
                if (e.target.checked) newParams.set('unassigned', 'true');
                else newParams.delete('unassigned');
                setSearchParams(newParams);
              }}
            />
            Unassigned
          </label>
        </div>

        {/* Content area — loading / error / empty / table. */}
        <FadeScroll style={{ flex: 1 }} fadeColor="var(--color-bg-primary)">
          {isLoading ? (
            <div style={{ padding: '16px' }}>
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton
                  key={i}
                  style={{ height: '40px', marginBottom: '8px', borderRadius: '4px' }}
                />
              ))}
            </div>
          ) : isError ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'var(--color-danger)',
                fontSize: '14px',
                padding: '32px',
                textAlign: 'center',
              }}
            >
              Could not load jobs. Retrying…
            </div>
          ) : visibleJobs.length === 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'var(--color-text-tertiary)',
                fontSize: '14px',
                padding: '32px',
              }}
            >
              <span>{emptyMessage}</span>
              {hasFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  style={{
                    padding: '6px 14px',
                    fontSize: '13px',
                    fontWeight: 600,
                    borderRadius: 'var(--radius-pill)',
                    border: '1px solid var(--color-accent-blue)',
                    backgroundColor: 'transparent',
                    color: 'var(--color-accent-blue)',
                    cursor: 'pointer',
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px',
                tableLayout: 'fixed',
              }}
            >
              {/* Column order (#2): checkbox · Customer · Site · Driver · Ticket ID ·
                  Created by · Requested · Due date · Type · Status · Actions. */}
              <colgroup>
                <col style={{ width: '32px' }} /> {/* checkbox */}
                <col style={{ width: '130px' }} /> {/* Customer */}
                <col style={{ width: '140px' }} /> {/* Site */}
                <col style={{ width: '110px' }} /> {/* Driver */}
                <col style={{ width: '115px' }} /> {/* Ticket ID */}
                <col style={{ width: '130px' }} /> {/* Requestor */}
                <col style={{ width: '120px' }} /> {/* Created by */}
                <col style={{ width: '92px' }} /> {/* Requested */}
                <col style={{ width: '100px' }} /> {/* Due date */}
                <col style={{ width: '110px' }} /> {/* Type */}
                <col style={{ width: '130px' }} /> {/* Status */}
                <col style={{ width: '72px' }} /> {/* Actions */}
              </colgroup>
              <thead
                style={{
                  position: 'sticky',
                  top: 0,
                  backgroundColor: 'var(--color-bg-secondary)',
                  zIndex: 1,
                }}
              >
                <tr>
                  {QUEUE_COLUMNS.map((col, i) => {
                    const sortable = col.sortKey !== null;
                    const active = sort !== null && sort.key === col.sortKey;
                    return (
                      <th
                        key={col.label || `col-${String(i)}`}
                        onClick={
                          sortable
                            ? (): void => {
                                toggleSort(col.sortKey as SortKey);
                              }
                            : undefined
                        }
                        aria-sort={
                          active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined
                        }
                        style={{
                          textAlign: 'left',
                          padding: '8px 6px',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: active
                            ? 'var(--color-accent-blue)'
                            : 'var(--color-text-secondary)',
                          borderBottom: '1px solid var(--color-border-primary)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: sortable ? 'pointer' : 'default',
                          userSelect: 'none',
                        }}
                      >
                        {col.label}
                        {active ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedJobs.map((job) => {
                  const isSelected = selectedIds.has(job.id);
                  const isRejected = job.statusCode === 'REJECTED';
                  const notesOpen = notesOpenIds.has(job.id);
                  const hasNotes = job.dispatcherNotes != null && job.dispatcherNotes.trim() !== '';
                  const sc = statusColor(job.statusCode);

                  const rowStyle: React.CSSProperties = {
                    backgroundColor: isSelected
                      ? 'var(--color-accent-blue-glow)'
                      : 'var(--color-bg-primary)',
                    borderLeft: isSelected
                      ? '3px solid var(--color-accent-blue)'
                      : isRejected
                        ? '3px solid var(--color-danger)'
                        : !job.driverId
                          ? '3px solid var(--color-warning)'
                          : '3px solid transparent',
                    cursor: 'pointer',
                  };

                  return (
                    <React.Fragment key={job.id}>
                      <tr
                        id={`job-row-${job.id}`}
                        tabIndex={-1}
                        data-testid={`job-row-${job.id}`}
                        data-status={job.statusCode}
                        data-rejected={isRejected ? 'true' : 'false'}
                        style={rowStyle}
                        onClick={(): void => {
                          setSelectedJobId(job.id);
                        }}
                        onMouseEnter={(e): void => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                          }
                        }}
                        onMouseLeave={(e): void => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                          }
                        }}
                      >
                        {/* Checkbox — stop row-click so selecting doesn't open detail */}
                        <td
                          style={{ padding: '8px 6px', width: '32px' }}
                          onClick={(e): void => {
                            e.stopPropagation();
                          }}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(): void => {
                              toggleSelect(job.id);
                            }}
                            aria-label={`Select job ${job.haulId ?? job.id}`}
                          />
                        </td>

                        {/* Customer (#3 — label only; job.operatorName unchanged) */}
                        <td title={job.operatorName} style={cellStyle}>
                          {job.operatorName}
                        </td>

                        {/* Site — Quick 260722: a link opening the SiteDetailModal.
                            Only clickable when the row carries a resolvable site id
                            (hauler_location siteId or a standalone-well pickupWellId). */}
                        <td title={job.siteName} style={cellStyle}>
                          {job.siteId != null || job.pickupWellId != null ? (
                            <button
                              type="button"
                              onClick={(e): void => {
                                e.stopPropagation();
                                setSelectedSite({
                                  siteId: job.siteId ?? null,
                                  pickupWellId: job.pickupWellId ?? null,
                                  siteName: job.siteName,
                                });
                              }}
                              style={{
                                padding: 0,
                                border: 'none',
                                background: 'none',
                                font: 'inherit',
                                color: 'var(--color-accent-blue)',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                              }}
                            >
                              {job.siteName}
                            </button>
                          ) : (
                            job.siteName
                          )}
                        </td>

                        {/* Driver — amber "Unassigned" badge when none */}
                        <td
                          style={{
                            ...cellStyle,
                            color:
                              job.driverName !== null
                                ? 'var(--color-text-primary)'
                                : 'var(--color-text-tertiary)',
                          }}
                        >
                          {job.driverName ?? (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '2px 6px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 600,
                                backgroundColor: 'rgba(246, 182, 94, 0.15)',
                                color: 'var(--color-warning)',
                              }}
                            >
                              Unassigned
                            </span>
                          )}
                        </td>

                        {/* Ticket ID (Haul ID) */}
                        <td
                          style={{
                            ...cellStyle,
                            fontFamily: 'var(--font-mono, monospace)',
                            fontSize: '12px',
                            color: 'var(--color-accent-blue)',
                          }}
                        >
                          <span title={job.haulId ?? undefined}>{job.haulId ?? '—'}</span>
                        </td>

                        {/* Requestor (86bazkeyn) — the customer contact, clickable through
                            to their detail popup. Plain text when the ticket carries no
                            linked requestor: the free-text requester fields are the day-one
                            fallback and there is nothing to open. */}
                        <td style={cellStyle} title={job.requestorName ?? undefined}>
                          {job.requestorName != null && job.requestorId != null ? (
                            <button
                              type="button"
                              onClick={(e): void => {
                                e.stopPropagation();
                                setRequestorPopup({
                                  id: job.requestorId ?? null,
                                  name: job.requestorName ?? null,
                                });
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                font: 'inherit',
                                color: 'var(--color-accent-blue)',
                                cursor: 'pointer',
                                textAlign: 'left',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {job.requestorName}
                            </button>
                          ) : (
                            <span style={{ color: 'var(--color-text-tertiary)' }}>—</span>
                          )}
                        </td>

                        {/* Created by (#4 — createdByName) */}
                        <td
                          title={job.createdByName ?? undefined}
                          style={{
                            ...cellStyle,
                            color:
                              job.createdByName != null
                                ? 'var(--color-text-primary)'
                                : 'var(--color-text-tertiary)',
                          }}
                        >
                          {job.createdByName ?? '—'}
                        </td>

                        {/* Requested (#5 — createdAt, label renamed from Created) */}
                        <td
                          style={{
                            ...cellStyle,
                            fontSize: '12px',
                            fontFamily: 'var(--font-mono, monospace)',
                            color: 'var(--color-text-secondary)',
                          }}
                        >
                          {formatTimestamp(job.createdAt)}
                        </td>

                        {/* Due date (#5 — scheduledAt, label renamed from Scheduled) */}
                        <td
                          style={{
                            ...cellStyle,
                            fontSize: '12px',
                            fontFamily: 'var(--font-mono, monospace)',
                            color: 'var(--color-text-secondary)',
                          }}
                        >
                          {job.scheduledAt ? formatTimestamp(job.scheduledAt) : '—'}
                        </td>

                        {/* Type */}
                        <td title={job.jobTypeLabel} style={cellStyle}>
                          {job.jobTypeLabel}
                        </td>

                        {/* Status (#6 — real colored badge column) + DEC-146 URGENT */}
                        <td style={cellStyle}>
                          <span
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                          >
                            {job.isUrgent === true && (
                              <span
                                data-testid="urgent-badge"
                                title="Urgent"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '2px 6px',
                                  borderRadius: '12px',
                                  fontSize: '10px',
                                  fontWeight: 700,
                                  letterSpacing: '0.05em',
                                  whiteSpace: 'nowrap',
                                  color: 'var(--color-warning)',
                                  backgroundColor: 'rgba(246, 182, 94, 0.18)',
                                  border: '1px solid rgba(246, 182, 94, 0.4)',
                                }}
                              >
                                URGENT
                              </span>
                            )}
                            <span
                              data-status-code={job.statusCode}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                color: sc.fg,
                                backgroundColor: sc.bg,
                                border: `1px solid ${sc.border}`,
                              }}
                            >
                              {statusFullLabel(job.statusCode)}
                            </span>
                          </span>
                        </td>

                        {/* Actions — notes toggle (all rows) + overflow menu (rejected). */}
                        <td
                          style={{ padding: '8px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}
                          onClick={(e): void => {
                            e.stopPropagation();
                          }}
                        >
                          <button
                            type="button"
                            aria-label={notesOpen ? 'Hide notes' : 'Edit notes'}
                            aria-expanded={notesOpen}
                            title={hasNotes ? 'Edit notes' : 'Add notes'}
                            onClick={(): void => {
                              toggleNotes(job.id);
                            }}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '28px',
                              height: '28px',
                              border: 'none',
                              borderRadius: '10px',
                              backgroundColor: notesOpen
                                ? 'var(--color-bg-tertiary)'
                                : 'transparent',
                              color: hasNotes
                                ? 'var(--color-accent-blue)'
                                : 'var(--color-text-tertiary)',
                              cursor: 'pointer',
                            }}
                          >
                            <StickyNote size={16} aria-hidden="true" />
                          </button>
                          {isRejected && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  aria-label="More actions"
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '28px',
                                    height: '28px',
                                    border: 'none',
                                    borderRadius: 'var(--radius-pill)',
                                    backgroundColor: 'transparent',
                                    color: 'var(--color-text-secondary)',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <MoreHorizontal size={16} aria-hidden="true" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {isElevated && (
                                  <DropdownMenuItem
                                    onClick={(): void => {
                                      setForceAssignJob({
                                        id: job.id,
                                        haulId: job.haulId ?? '—',
                                        operatorRequestNotes: job.operatorRequestNotes ?? null,
                                      });
                                    }}
                                  >
                                    Force Assign Back
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </td>
                      </tr>

                      {/* DEC-299 / Task 18.6 — the OPERATOR's note on an API haul request.
                          ALWAYS visible (not behind the notes toggle) so it is in front of the
                          dispatcher at the moment they assign; read-only and labelled, and
                          separate from the dispatcher's own notes editor below. */}
                      {hasOperatorRequestNote(job.operatorRequestNotes) && (
                        <tr data-testid={`job-operator-note-row-${job.id}`}>
                          <td
                            colSpan={COLUMN_COUNT}
                            style={{
                              padding: '0 12px 8px 12px',
                              backgroundColor: 'var(--color-bg-primary)',
                              borderBottom: '1px solid var(--color-border-primary)',
                            }}
                          >
                            <OperatorRequestNote
                              notes={job.operatorRequestNotes}
                              testId={`job-operator-note-${job.id}`}
                            />
                          </td>
                        </tr>
                      )}

                      {/* Inline notes editor — expands full-width below the row (#8). */}
                      {notesOpen && (
                        <tr data-testid={`job-notes-${job.id}`}>
                          <td
                            colSpan={COLUMN_COUNT}
                            style={{
                              padding: '6px 12px 12px 12px',
                              backgroundColor: 'var(--color-bg-secondary)',
                              borderBottom: '1px solid var(--color-border-primary)',
                            }}
                            onClick={(e): void => {
                              e.stopPropagation();
                            }}
                          >
                            <JobNotesEditor
                              jobId={job.id}
                              initialNotes={job.dispatcherNotes ?? ''}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </FadeScroll>

        {/* Bulk assign bar — shown when at least one row is selected */}
        {selectedIds.size > 0 && (
          <BulkAssignBar
            selectedJobIds={Array.from(selectedIds)}
            onCancel={clearSelection}
            onSuccess={clearSelection}
          />
        )}

        {/* Force-assign modal */}
        {forceAssignJob !== null && (
          <ForceAssignModal
            job={forceAssignJob}
            open={true}
            onOpenChange={(open): void => {
              if (!open) setForceAssignJob(null);
            }}
          />
        )}

        {/* Job detail modal — opened by a row click */}
        <JobDetailModal
          jobId={selectedJobId}
          onClose={(): void => {
            setSelectedJobId(null);
          }}
        />

        {/* Site detail popup — opened by a Site link (Quick 260722) */}
        <SiteDetailModal
          site={selectedSite}
          onClose={(): void => {
            setSelectedSite(null);
          }}
        />

        <RequestorDetailModal
          requestorId={requestorPopup?.id ?? null}
          fallbackName={requestorPopup?.name ?? null}
          onClose={(): void => {
            setRequestorPopup(null);
          }}
        />
      </div>
    </TooltipProvider>
  );
}
